import { Button } from "@/shared/components/ui/button";
import { Bell, HelpCircle, LogOut, User, Shield } from "lucide-react";
import { useAuth } from "@/core/auth/AuthProvider";
import { useTenant } from "@/contexts/TenantContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { RoleImpersonator } from "./RoleImpersonator";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { Avatar, AvatarFallback } from "@/shared/components/ui/avatar";
import { Badge } from "@/shared/components/ui/badge";
import { SidebarTrigger } from "@/shared/components/ui/sidebar";
import { Link } from "react-router-dom";
import { ThemeToggle } from "./ThemeToggle";
import { ContextPicker } from "./ContextPicker";
import { Breadcrumbs } from "./Breadcrumbs";
import { DrPhillipTrigger } from "@/features/dashboard/components/DrPhillipTrigger";
import { cn } from "@/shared/utils/cn";

export function Header() {
  const { profile, roles, signOut, hasRole } = useAuth();
  const { isAdminMode, setAdminMode } = useTenant();

  const getRoleLabel = () => {
    if (roles.some(r => r.role === 'admin')) return 'Admin';
    if (roles.some(r => r.role === 'tech_support')) return 'Tech Support';
    if (roles.some(r => r.role === 'agency_owner')) return 'Agency Owner';
    if (roles.some(r => r.role === 'company_owner')) return 'Company Owner';
    if (roles.some(r => r.role === 'developer')) return 'Developer';
    if (roles.some(r => r.role === 'call_center')) return 'Call Center';
    return 'User';
  };

  const getInitials = () => {
    if (!profile?.full_name) return profile?.email?.charAt(0).toUpperCase() || 'U';
    return profile.full_name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
      <header className="sticky top-0 z-30 h-14 border-b border-border/50 bg-white/95 dark:bg-[rgba(6,11,24,0.85)] backdrop-blur-md">
      <div className="flex h-full items-center justify-between px-2 md:px-6 gap-1 md:gap-4">
        <div className="flex-1 flex items-center gap-2 md:gap-4 min-w-0">
          {/* Sidebar Toggle */}
          <SidebarTrigger className="-ml-1 h-10 w-10 md:h-9 md:w-9 shrink-0" />

          {/* Unified Agency > Client selector (only in Client View) */}
          {!isAdminMode && <ContextPicker />}

          {/* Breadcrumbs */}
          <div className="flex-1 min-w-0 hidden sm:block">
            <Breadcrumbs />
          </div>
        </div>

        <div className="flex items-center gap-1 md:gap-2">
          {/* Theme Toggle */}
          <ThemeToggle />

          <DrPhillipTrigger />

          <Button variant="ghost" size="icon" className="hidden lg:flex h-9 w-9" asChild>
            <Link to="/docs" title="Help">
              <HelpCircle className="h-5 w-5" />
            </Link>
          </Button>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="icon" className="h-9 w-9" aria-label="Notifications">
                <Bell className="h-5 w-5" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80" align="end">
              <p className="text-sm text-muted-foreground text-center py-4">No notifications yet</p>
            </PopoverContent>
          </Popover>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="relative h-9 w-9 md:h-10 md:w-10 rounded-full p-0"
              >
                <Avatar className="h-9 w-9 md:h-10 md:w-10">
                  <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                    {getInitials()}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium">{profile?.full_name || "User"}</p>
                  <p className="text-xs text-muted-foreground">{profile?.email}</p>
                  <Badge variant="secondary" className="w-fit mt-1">
                    {getRoleLabel()}
                  </Badge>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {hasRole("admin") && (
                <>
                  <div className="px-2 py-2 flex items-center justify-between gap-2">
                    <span className="text-sm flex items-center gap-2">
                      <Shield className="h-4 w-4 text-amber-500" />
                      Admin View
                    </span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={isAdminMode}
                      onClick={() => setAdminMode(!isAdminMode)}
                      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background ${
                        isAdminMode ? "bg-amber-500" : "bg-primary"
                      }`}
                    >
                      <span
                        className={`pointer-events-none block h-5 w-5 rounded-full bg-white shadow-lg ring-0 transition-transform ${
                          isAdminMode ? "translate-x-5" : "translate-x-0.5"
                        }`}
                      />
                    </button>
                  </div>
                  <RoleImpersonator />
                  <DropdownMenuSeparator />
                </>
              )}
              <DropdownMenuItem>
                <User className="mr-2 h-4 w-4" />
                Profile
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={signOut} className="text-destructive">
                <LogOut className="mr-2 h-4 w-4" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      </header>
  );
}
